import type { PdfExportConfig } from "./PdfExportConfig";
import { PrefixMode } from "../models/SettingsEnums";
import { modelHeaderText, modelHeaderTag, type ModelHeaderContext, type ModelHeaderSource } from "../models/ModelHeader";

type PdfHeaderSource = ModelHeaderSource & {
  headerText?: (context: ModelHeaderContext) => string;
  headerTag?: (context: ModelHeaderContext) => string;
};

export type PdfLabelContext = ModelHeaderContext & { useTags?: boolean };

/** Resolves document formatting without coupling the model to application settings. */
export function pdfHeaderContext(): ModelHeaderContext {
  return {
    useDotSeparator: pdfTagNameUsesDot(),
    useDinConvention: (globalThis.state?.settings?.prefixMode ?? PrefixMode.DIN) === PrefixMode.DIN,
  };
}

/** Uses the model API, with the same formatting for immutable report snapshots. */
export function pdfHeaderText(object: PdfHeaderSource | undefined | null, context: PdfLabelContext = pdfHeaderContext()): string {
  if (!object) return "";
  if (!(context.useTags ?? pdfUsesTags())) return (object.nameOfDefaultName ?? object.name ?? "").trim();
  return object.headerText ? object.headerText(context) : modelHeaderText(object, context);
}

/** Uses the model API for standalone tags in document tables and diagrams. */
export function pdfHeaderTag(object: PdfHeaderSource | undefined | null, context: PdfLabelContext = pdfHeaderContext()): string {
  if (!object) return "";
  if (!(context.useTags ?? pdfUsesTags())) return "";
  return object.headerTag ? object.headerTag(context) : modelHeaderTag(object, context);
}

/** Resolves the current application's saved document-wide typography preference. */
function currentPdfConfig(): PdfExportConfig | undefined {
  const app = globalThis.state;
  return app?.settings?.applicationMode === "c1"
    ? app.c1Project?.masterPdfConfig : app?.settings?.masterPdfConfig;
}

/** Keeps the global tag choice consistent across document headings and table labels. */
export function pdfUsesTags(): boolean {
  return currentPdfConfig()?.useTags ?? true;
}

/** Sorts document pages by the visible tag, or by name when tags are hidden. */
export function comparePdfLabels(left: PdfHeaderSource | undefined, right: PdfHeaderSource | undefined, language = "de", useTags = pdfUsesTags()): number {
  const leftName = left?.nameOfDefaultName ?? left?.name ?? "";
  const rightName = right?.nameOfDefaultName ?? right?.name ?? "";
  const tagOrder = useTags ? pdfHeaderTag(left).localeCompare(pdfHeaderTag(right), language, { numeric: true }) : 0;
  return tagOrder || leftName.localeCompare(rightName, language, { numeric: true });
}

/** Applies the saved separator only when both tag and name are shown. */
export function pdfTagNameUsesDot(): boolean {
  return currentPdfConfig()?.separateTagAndName ?? true;
}
